// src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Badge,
    Button,
    Spinner,
    Alert,
    Tabs,
    Tab,
    Form,
    InputGroup
} from 'react-bootstrap';
import {
    PeopleFill,
    FileEarmarkTextFill,
    LightningChargeFill,
    ArrowRight,
    Search,
    SortDown,
    SortUp
} from 'react-bootstrap-icons';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import {
    AreaChart,
    Area,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statsData, setStatsData] = useState(null);
    const [users, setUsers] = useState([]);
    const [flightSheets, setFlightSheets] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [userSearch, setUserSearch] = useState('');
    const [flightSheetSearch, setFlightSheetSearch] = useState('');
    const [sortConfig, setSortConfig] = useState({
        field: 'createdAt',
        direction: 'desc'
    });

    // Проверка прав администратора
    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Загрузка данных для админки
    useEffect(() => {
        const fetchAdminData = async () => {
            try {
                // Загружаем глобальную статистику
                const statsResponse = await api.get('/api/admin/stats');
                setStatsData(statsResponse.data.data);

                // Загружаем пользователей
                const usersResponse = await api.get('/api/admin/users');
                setUsers(usersResponse.data.data);

                // Загружаем полетные листы
                const flightSheetsResponse = await api.get('/api/admin/flight-sheets');
                setFlightSheets(flightSheetsResponse.data.data);

                setLoading(false);
            } catch (err) {
                setError(
                    err.response?.data?.message ||
                    t('errors.loadingError')
                );
                setLoading(false);
            }
        };

        if (user && user.isAdmin) {
            fetchAdminData();
        }
    }, [user, t]);

    // Сортировка и фильтрация данных
    const getSortedUsers = () => {
        if (!users) return [];

        // Фильтрация по поиску
        const filteredUsers = users.filter(user =>
            user.username.toLowerCase().includes(userSearch.toLowerCase())
        );

        // Сортировка
        if (sortConfig.field) {
            return [...filteredUsers].sort((a, b) => {
                const aValue = a[sortConfig.field];
                const bValue = b[sortConfig.field];

                if (sortConfig.direction === 'asc') {
                    if (aValue < bValue) return -1;
                    if (aValue > bValue) return 1;
                    return 0;
                } else {
                    if (aValue < bValue) return 1;
                    if (aValue > bValue) return -1;
                    return 0;
                }
            });
        }

        return filteredUsers;
    };

    const getSortedFlightSheets = () => {
        if (!flightSheets) return [];

        // Фильтрация по поиску
        const filteredFlightSheets = flightSheets.filter(fs =>
            fs.name.toLowerCase().includes(flightSheetSearch.toLowerCase()) ||
            fs.minerWorker.toLowerCase().includes(flightSheetSearch.toLowerCase()) ||
            (fs.user && fs.user.username.toLowerCase().includes(flightSheetSearch.toLowerCase()))
        );

        // Сортировка
        if (sortConfig.field) {
            return [...filteredFlightSheets].sort((a, b) => {
                let aValue, bValue;

                // Для вложенных полей (user.username)
                if (sortConfig.field === 'user.username') {
                    aValue = a.user ? a.user.username : '';
                    bValue = b.user ? b.user.username : '';
                } else {
                    aValue = a[sortConfig.field];
                    bValue = b[sortConfig.field];
                }

                if (sortConfig.direction === 'asc') {
                    if (aValue < bValue) return -1;
                    if (aValue > bValue) return 1;
                    return 0;
                } else {
                    if (aValue < bValue) return 1;
                    if (aValue > bValue) return -1;
                    return 0;
                }
            });
        }

        return filteredFlightSheets;
    };

    // Обработчик изменения сортировки
    const handleSort = (field) => {
        if (sortConfig.field === field) {
            // Если уже сортируем по этому полю, меняем направление
            setSortConfig({
                field,
                direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
            });
        } else {
            // Иначе сортируем по новому полю по убыванию
            setSortConfig({
                field,
                direction: 'desc'
            });
        }
    };

    // Компонент для отрисовки иконки сортировки
    const SortIcon = ({ field }) => {
        if (sortConfig.field !== field) {
            return null;
        }

        return sortConfig.direction === 'asc' ? <SortUp /> : <SortDown />;
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString();
    };

    // Функция для форматирования хешрейта
    const formatHashrate = (hashrate) => {
        if (!hashrate) return '0 H/s';

        if (hashrate < 1000) {
            return `${hashrate.toFixed(2)} H/s`;
        } else if (hashrate < 1000000) {
            return `${(hashrate / 1000).toFixed(2)} KH/s`;
        } else if (hashrate < 1000000000) {
            return `${(hashrate / 1000000).toFixed(2)} MH/s`;
        } else if (hashrate < 1000000000000) {
            return `${(hashrate / 1000000000).toFixed(2)} GH/s`;
        } else {
            return `${(hashrate / 1000000000000).toFixed(2)} TH/s`;
        }
    };

    // Форматирование времени для графиков
    const formatTimeForChart = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleTimeString();
    };

    if (loading) {
        return (
            <Container className="my-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">{t('common.loading')}</p>
            </Container>
        );
    }

    if (!user || !user.isAdmin) {
        return (
            <Container className="my-5">
                <Alert variant="danger">
                    <Alert.Heading>{t('admin.accessDenied')}</Alert.Heading>
                    <p>{t('admin.adminRightsRequired')}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="outline-danger"
                            onClick={() => navigate('/dashboard')}
                        >
                            {t('admin.returnToDashboard')}
                        </Button>
                    </div>
                </Alert>
            </Container>
        );
    }

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

    return (
        <Container fluid className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{t('admin.panel')}</h1>
                <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard')}
                >
                    {t('admin.returnToDashboard')}
                </Button>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
            >
                <Tab eventKey="overview" title={t('admin.overview')}>
                    {/* Статистика */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <Card className="shadow-sm h-100">
                                <Card.Body className="text-center">
                                    <div className="display-4 text-primary mb-2">
                                        <PeopleFill />
                                    </div>
                                    <h6 className="text-muted mb-2">{t('admin.totalUsers')}</h6>
                                    <h2>{statsData?.totalUsers || 0}</h2>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="shadow-sm h-100">
                                <Card.Body className="text-center">
                                    <div className="display-4 text-success mb-2">
                                        <FileEarmarkTextFill />
                                    </div>
                                    <h6 className="text-muted mb-2">{t('admin.totalFlightSheets')}</h6>
                                    <h2>{statsData?.totalFlightSheets || 0}</h2>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="shadow-sm h-100">
                                <Card.Body className="text-center">
                                    <div className="display-4 text-warning mb-2">
                                        <LightningChargeFill />
                                    </div>
                                    <h6 className="text-muted mb-2">{t('admin.activeWorkers')}</h6>
                                    <h2>{statsData?.activeWorkers || 0}</h2>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="shadow-sm h-100">
                                <Card.Body className="text-center">
                                    <h6 className="text-muted mb-2">{t('dashboard.totalHashrate')}</h6>
                                    <h2 className="text-primary">
                                        {statsData?.hashrateFormatted || '0 H/s'}
                                    </h2>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Графики */}
                    <Row className="mb-4">
                        <Col md={8}>
                            <Card className="shadow-sm h-100">
                                <Card.Header>
                                    <h5 className="mb-0">{t('charts.hashrateChart')} (24 {t('charts.hours')})</h5>
                                </Card.Header>
                                <Card.Body>
                                    {statsData?.hashrateHistory?.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <AreaChart
                                                data={statsData.hashrateHistory}
                                                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="timestamp"
                                                    tickFormatter={formatTimeForChart}
                                                    interval="preserveEnd"
                                                />
                                                <YAxis />
                                                <Tooltip
                                                    formatter={(value) => [formatHashrate(value), t('charts.hashrate')]}
                                                    labelFormatter={formatTimeForChart}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="hashrate"
                                                    stroke="#8884d8"
                                                    fill="#8884d8"
                                                    fillOpacity={0.3}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="text-center py-5">
                                            <p className="text-muted">{t('charts.noData')}</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm h-100">
                                <Card.Header>
                                    <h5 className="mb-0">{t('dashboard.shares')} (24 {t('charts.hours')})</h5>
                                </Card.Header>
                                <Card.Body>
                                    {statsData?.shares ? (
                                        <React.Fragment>
                                            <div className="text-center">
                                                <h3>
                                                    {statsData.shares.acceptanceRate}
                                                </h3>
                                                <p className="text-muted">{t('dashboard.acceptanceRate')}</p>
                                            </div>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: t('dashboard.accepted'), value: statsData.shares.accepted },
                                                            { name: t('dashboard.rejected'), value: statsData.shares.rejected }
                                                        ]}
                                                        cx="50%"
                                                        cy="50%"
                                                        labelLine={false}
                                                        outerRadius={80}
                                                        fill="#8884d8"
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                    >
                                                        <Cell fill="#28a745" />
                                                        <Cell fill="#dc3545" />
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                            <div className="text-center mt-2">
                                                <div className="d-flex justify-content-center">
                                                    <div className="me-3">
                                                        <Badge bg="success" className="me-1"></Badge>
                                                        <span>{t('dashboard.accepted')}: {statsData.shares.accepted}</span>
                                                    </div>
                                                    <div>
                                                        <Badge bg="danger" className="me-1"></Badge>
                                                        <span>{t('dashboard.rejected')}: {statsData.shares.rejected}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    ) : (
                                        <div className="text-center py-5">
                                            <p className="text-muted">{t('charts.noData')}</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Топ пользователей */}
                    <Card className="shadow-sm mb-4">
                        <Card.Header>
                            <h5 className="mb-0">{t('admin.topUsersByHashrate')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {statsData?.topUsersByHashrate?.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                        <tr>
                                            <th>{t('common.user')}</th>
                                            <th>{t('dashboard.hashrate')}</th>
                                            <th>{t('common.actions')}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {statsData.topUsersByHashrate.map((user, index) => (
                                            <tr key={user._id}>
                                                <td>
                                                    <strong>{user.username}</strong>
                                                    {index === 0 && (
                                                        <Badge bg="warning" className="ms-2">Top</Badge>
                                                    )}
                                                </td>
                                                <td className="text-primary">
                                                    <strong>{user.hashrateFormatted}</strong>
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => {
                                                            setActiveTab('users');
                                                            setUserSearch(user.username);
                                                        }}
                                                    >
                                                        <ArrowRight /> {t('common.view')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="mb-0 text-muted">{t('charts.noData')}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="users" title={t('admin.users')}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">{t('admin.usersList')}</h5>
                                <Form.Group className="mb-0" style={{ width: '300px' }}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <Search />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('admin.userSearch')}
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {getSortedUsers().length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                        <tr>
                                            <th className="cursor-pointer" onClick={() => handleSort('username')}>
                                                {t('common.user')} <SortIcon field="username" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('isAdmin')}>
                                                {t('admin.permissions')} <SortIcon field="isAdmin" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('flightSheetsCount')}>
                                                {t('dashboard.flightSheets')} <SortIcon field="flightSheetsCount" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('activeWorkers')}>
                                                {t('admin.workerCount')} <SortIcon field="activeWorkers" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('totalHashrate')}>
                                                {t('dashboard.hashrate')} <SortIcon field="totalHashrate" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('createdAt')}>
                                                {t('admin.registrationDate')} <SortIcon field="createdAt" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('lastLogin')}>
                                                {t('admin.lastLogin')} <SortIcon field="lastLogin" />
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {getSortedUsers().map(user => (
                                            <tr key={user._id}>
                                                <td>
                                                    <strong>{user.username}</strong>
                                                </td>
                                                <td>
                                                    {user.isAdmin ? (
                                                        <Badge bg="warning">{t('common.admin')}</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">{t('common.user')}</Badge>
                                                    )}
                                                </td>
                                                <td>{user.flightSheetsCount}</td>
                                                <td>
                                                    <Badge bg={user.activeWorkers > 0 ? 'success' : 'secondary'}>
                                                        {user.activeWorkers}
                                                    </Badge>
                                                </td>
                                                <td className="text-primary">
                                                    <strong>{user.hashrateFormatted}</strong>
                                                </td>
                                                <td>{formatDate(user.createdAt)}</td>
                                                <td>{formatDate(user.lastLogin)}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    {userSearch ? (
                                        <p className="mb-0 text-muted">{t('charts.noData')}</p>
                                    ) : (
                                        <p className="mb-0 text-muted">{t('charts.noData')}</p>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="flightSheets" title={t('dashboard.flightSheets')}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">{t('admin.flightSheetsList')}</h5>
                                <Form.Group className="mb-0" style={{ width: '300px' }}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <Search />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('admin.flightSheetSearch')}
                                            value={flightSheetSearch}
                                            onChange={(e) => setFlightSheetSearch(e.target.value)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {getSortedFlightSheets().length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                        <tr>
                                            <th className="cursor-pointer" onClick={() => handleSort('name')}>
                                                {t('common.name')} <SortIcon field="name" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('user.username')}>
                                                {t('common.user')} <SortIcon field="user.username" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('minerWorker')}>
                                                minerWorker <SortIcon field="minerWorker" />
                                            </th>
                                            <th>Pool</th>
                                            <th className="cursor-pointer" onClick={() => handleSort('activeWorkers')}>
                                                {t('admin.workerCount')} <SortIcon field="activeWorkers" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('totalHashrate')}>
                                                {t('dashboard.hashrate')} <SortIcon field="totalHashrate" />
                                            </th>
                                            <th className="cursor-pointer" onClick={() => handleSort('createdAt')}>
                                                {t('flightSheet.createdAt')} <SortIcon field="createdAt" />
                                            </th>
                                            <th>{t('common.status')}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {getSortedFlightSheets().map(flightSheet => (
                                            <tr key={flightSheet._id}>
                                                <td>
                                                    <strong>{flightSheet.name}</strong>
                                                </td>
                                                <td>
                                                    {flightSheet.user ? flightSheet.user.username : 'Н/Д'}
                                                </td>
                                                <td>
                                                    <code>{flightSheet.minerWorker}</code>
                                                </td>
                                                <td>
                                                    {flightSheet.poolHost}:{flightSheet.poolPort}
                                                    <div>
                                                        <small className="text-muted">
                                                            {flightSheet.poolWorker}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg={flightSheet.activeWorkers > 0 ? 'success' : 'secondary'}>
                                                        {flightSheet.activeWorkers}
                                                    </Badge>
                                                </td>
                                                <td className="text-primary">
                                                    <strong>{flightSheet.hashrateFormatted}</strong>
                                                </td>
                                                <td>{formatDate(flightSheet.createdAt)}</td>
                                                <td>
                                                    <Badge bg={flightSheet.active ? 'success' : 'danger'}>
                                                        {flightSheet.active ? t('common.online') : t('common.offline')}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    {flightSheetSearch ? (
                                        <p className="mb-0 text-muted">{t('charts.noData')}</p>
                                    ) : (
                                        <p className="mb-0 text-muted">{t('charts.noData')}</p>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </Container>
    );
};

export default AdminDashboard;